import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createTrialTenant, previewTenantSlug } from '../api/authApi';
import { getPublicRegisterModules, getPublicRegisterOptions, previewPublicRegister } from '../api/publicRegistrationApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DetailField } from '../components/InfoCard';
import { PageHeader } from '../components/PageHeader';
import { RegisterCustomerFields } from '../components/register/RegisterCustomerFields';
import { SectionCard } from '../components/SectionCard';
import { Button } from '../components/ui/Button';
import { Select, SelectOption } from '../components/ui/Select';
import { TextInput } from '../components/ui/TextInput';
import { resolvePhonePrefixCountryCode } from '../utils/countryDisplay';
import {
  FALLBACK_REGISTER_OPTIONS,
  emptyRegisterCustomerForm,
  findCountryByCode,
  isValidNationalPhoneInput,
} from '../utils/registerCustomerForm';
import {
  buildRegisterCustomerPayload,
  resolveSlugSourceName,
  validateCompanyStep,
} from '../utils/registerWizardModel';

const { semantic } = colorTokens;

const STEP_KEYS = ['customer', 'modules', 'tenant', 'review'];

const initialForm = {
  ...emptyRegisterCustomerForm(),
  tenant_name: '',
  tenant_slug: '',
  primary_domain: '',
  billing: 'monthly',
  trial_days: 30,
  start_mode: 'trial',
  billing_amount: '',
};

const fieldGap = 2.5;

function WizardFooter({
  activeStep,
  submitting,
  canSubmit,
  onBack,
  onNext,
  onSubmit,
  backLabel,
  nextLabel,
  submitLabel,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        mt: 3.5,
        pt: 2.5,
        borderTop: `1px solid ${semantic.border.subtle}`,
      }}
    >
      <Button
        variant="Ghost"
        disabled={activeStep === 0 || submitting}
        onClick={onBack}
      >
        {backLabel}
      </Button>
      {activeStep < STEP_KEYS.length - 1 ? (
        <Button variant="Primary" onClick={onNext}>
          {nextLabel}
        </Button>
      ) : (
        <Button
          variant="Accent"
          onClick={onSubmit}
          disabled={!canSubmit}
          loading={submitting}
        >
          {submitLabel}
        </Button>
      )}
    </Box>
  );
}

export function TrialOnboardingPage() {
  const { t } = useTranslation(['onboarding', 'auth', 'common', 'register']);
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [registerOptions, setRegisterOptions] = useState(FALLBACK_REGISTER_OPTIONS);
  const [optionsWarning, setOptionsWarning] = useState(false);
  const [phonePrefixManuallyEdited, setPhonePrefixManuallyEdited] = useState(false);
  const [phoneValidationError, setPhoneValidationError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [stepErrors, setStepErrors] = useState({});
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [pricePreview, setPricePreview] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const steps = useMemo(
    () => STEP_KEYS.map((key) => t(`onboarding:trial.steps.${key}`)),
    [t],
  );

  useEffect(() => {
    getPublicRegisterModules()
      .then((modules) => setModuleCatalog(Array.isArray(modules) ? modules : []))
      .catch(() => setModuleCatalog([]));
  }, []);

  useEffect(() => {
    getPublicRegisterOptions()
      .then((options) => {
        setRegisterOptions(options);
        setOptionsWarning(false);
        const defaultCountry = findCountryByCode(options.countries, options.default_country_code);
        setForm((prev) => ({
          ...prev,
          country_code: options.default_country_code || prev.country_code,
          country_name: defaultCountry?.name || prev.country_name,
          phone_country_code: options.default_phone_country_code || prev.phone_country_code,
          locale: options.default_locale || prev.locale,
          timezone: options.default_timezone || prev.timezone,
          billing_currency: options.default_billing_currency || prev.billing_currency,
        }));
      })
      .catch(() => {
        setRegisterOptions(FALLBACK_REGISTER_OPTIONS);
        setOptionsWarning(true);
      });
  }, []);

  useEffect(() => {
    if (selectedModules.length === 0) {
      setPricePreview(null);
      return undefined;
    }

    const timer = setTimeout(() => {
      previewPublicRegister({
        modules: selectedModules.join(','),
        billing: form.billing,
        users: 10,
      })
        .then((data) => setPricePreview(data))
        .catch(() => setPricePreview(null));
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedModules, form.billing]);

  const loadPreview = useCallback(async (params) => {
    setPreviewLoading(true);
    try {
      const data = await previewTenantSlug(params);
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    const slugSource = resolveSlugSourceName(form);
    if (!form.tenant_name && !form.tenant_slug && !slugSource) {
      return undefined;
    }

    const timer = setTimeout(() => {
      loadPreview({
        company_name: slugSource || undefined,
        tenant_name: form.tenant_name || undefined,
        tenant_slug: form.tenant_slug || undefined,
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [form, loadPreview]);

  const isConsumer = form.customer_type === 'consumer';

  const clearStepError = (field) => {
    setStepErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    clearStepError(field);
  };

  const updateCompanyName = (event) => {
    setForm((prev) => ({ ...prev, company_name: event.target.value }));
    clearStepError('company_name');
  };

  const handleCustomerTypeChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      customer_type: value,
      ...(value === 'consumer'
        ? { company_id_number: '', tax_id: '', vat_id: '' }
        : {}),
    }));
    clearStepError('customer_type');
    clearStepError('company_name');
  };

  const handleCountryChange = (_event, country) => {
    if (!country) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      country_code: country.code,
      country_name: country.name,
      phone_country_code: phonePrefixManuallyEdited
        ? prev.phone_country_code
        : (country.phone_code || prev.phone_country_code),
    }));
  };

  const phonePrefixCountries = registerOptions.countries || FALLBACK_REGISTER_OPTIONS.countries;

  const handlePhonePrefixChange = (event) => {
    const country = findCountryByCode(phonePrefixCountries, event.target.value);
    if (!country) {
      return;
    }
    setPhonePrefixManuallyEdited(true);
    setForm((prev) => ({ ...prev, phone_country_code: country.phone_code }));
  };

  const handlePhoneNumberChange = (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, phone_national_number: value }));
    const invalid = !isValidNationalPhoneInput(value);
    setPhoneValidationError(invalid ? t('register:phoneInvalid') : '');
    if (!invalid) {
      clearStepError('phone_national_number');
    }
  };

  const handleConsentChange = (event) => {
    setConsentAccepted(event.target.checked);
    if (event.target.checked) {
      clearStepError('consent');
    }
  };

  const selectedCountry = useMemo(
    () => findCountryByCode(registerOptions.countries || [], form.country_code),
    [registerOptions.countries, form.country_code],
  );

  const phonePrefixCountryCode = useMemo(
    () => resolvePhonePrefixCountryCode(phonePrefixCountries, form.country_code, form.phone_country_code),
    [phonePrefixCountries, form.country_code, form.phone_country_code],
  );

  const handleNext = () => {
    if (activeStep === 0) {
      const phoneInvalid = String(form.phone_national_number || '').trim()
        && !isValidNationalPhoneInput(form.phone_national_number);
      const errors = validateCompanyStep({ form, consentAccepted, phoneInvalid });
      setStepErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }
    }
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);

    try {
      const payload = {
        ...buildRegisterCustomerPayload(form, { selectedCountry, registerOptions }),
        tenant_name: form.tenant_name,
        tenant_slug: form.tenant_slug || preview?.suggested_slug,
        primary_domain: form.primary_domain || undefined,
        modules: selectedModules.join(','),
        billing: form.billing,
        users_count: 10,
        trial_days: form.start_mode === 'direct' ? undefined : (Number(form.trial_days) || 30),
        start_mode: form.start_mode || 'trial',
        billing_amount: form.start_mode === 'direct' ? form.billing_amount : undefined,
      };

      const data = await createTrialTenant(payload);
      setResult(data);
      setActiveStep(steps.length);
    } catch (err) {
      const message = err.response?.data?.message
        || Object.values(err.response?.data?.errors ?? {}).flat().join(' ')
        || t('onboarding:trial.failed');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const emptyValue = t('common:empty_value');

  if (result) {
    return (
      <Box>
        <PageHeader
          title={t('onboarding:trial.complete_title')}
          subtitle={t('onboarding:trial.complete_subtitle')}
        />
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('onboarding:trial.complete_success')}
        </Alert>
        {(result.warnings ?? []).map((warning) => (
          <Alert key={warning} severity="warning" sx={{ mb: 1 }}>
            {warning}
          </Alert>
        ))}
        <SectionCard variant="Default" title={t('onboarding:trial.created_resources')} sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <DetailField
              label={t('onboarding:trial.customer_label')}
              value={result.customer?.name || emptyValue}
            />
            <DetailField
              label={t('onboarding:trial.tenant_label')}
              value={`${result.tenant?.name || emptyValue} (${result.tenant?.slug || emptyValue})`}
            />
            <DetailField
              label={t('onboarding:trial.domain_label')}
              value={result.domain?.host || emptyValue}
            />
            <DetailField
              label={t('onboarding:trial.database_record')}
              value={`${result.tenant_database?.db_name || emptyValue} (${result.tenant_database?.status || emptyValue})`}
            />
            <DetailField
              label={t('onboarding:trial.subscription_label')}
              value={`${result.subscription?.status || emptyValue} — ${result.subscription?.plan?.name ?? result.subscription?.plan_id ?? emptyValue}`}
            />
            <DetailField
              label={t('onboarding:trial.admin_label')}
              value={`${result.customer_admin?.email || emptyValue} (${result.customer_admin?.action || emptyValue})`}
            />
          </Box>
        </SectionCard>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            component={RouterLink}
            to={`/customers/${result.customer?.id}`}
            variant="Accent"
          >
            {t('onboarding:trial.customer_detail')}
          </Button>
          <Button
            component={RouterLink}
            to={`/tenants/${result.tenant?.id}`}
            variant="Secondary"
          >
            {t('onboarding:trial.tenant_detail')}
          </Button>
          <Button component={RouterLink} to="/subscriptions" variant="Secondary">
            {t('onboarding:trial.subscriptions')}
          </Button>
          <Button
            variant="Ghost"
            onClick={() => {
              setResult(null);
              setForm(initialForm);
              setSelectedModules([]);
              setPricePreview(null);
              setActiveStep(0);
            }}
          >
            {t('onboarding:trial.start_another')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t('onboarding:trial.title')}
        subtitle={t('onboarding:trial.subtitle')}
      />

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 3,
          px: { xs: 0, sm: 1 },
          '& .MuiStepConnector-line': {
            borderColor: semantic.border.subtle,
            borderTopWidth: 2,
          },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: colorTokens.brand.primary,
          },
          '& .MuiStepLabel-label': {
            mt: 1,
            fontFamily: typographyTokens.fontFamily,
            fontSize: 12,
            fontWeight: typographyTokens.fontWeightSemibold,
            letterSpacing: '0.02em',
            color: semantic.text.muted,
            '&.Mui-active': {
              color: semantic.text.primary,
            },
            '&.Mui-completed': {
              color: semantic.text.secondary,
            },
          },
          '& .MuiStepIcon-root': {
            color: semantic.bg.input,
            border: `1px solid ${semantic.border.default}`,
            borderRadius: '50%',
            fontSize: 28,
            '&.Mui-active': {
              color: colorTokens.brand.primary,
              borderColor: colorTokens.brand.primary,
            },
            '&.Mui-completed': {
              color: colorTokens.brand.primary,
              borderColor: colorTokens.brand.primary,
            },
          },
          '& .MuiStepIcon-text': {
            fill: semantic.text.primary,
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightBold,
            fontSize: '0.75rem',
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard
        variant="Default"
        title={steps[activeStep]}
        sx={{ mb: 0, width: '100%', maxWidth: '100%' }}
      >
        {activeStep === 0 && (
          <RegisterCustomerFields
            form={form}
            stepErrors={stepErrors}
            optionsWarning={optionsWarning}
            registerOptions={registerOptions}
            selectedCountry={selectedCountry}
            phonePrefixCountries={phonePrefixCountries}
            phonePrefixCountryCode={phonePrefixCountryCode}
            phoneValidationError={phoneValidationError}
            consentAccepted={consentAccepted}
            onFieldChange={updateField}
            onCompanyNameChange={updateCompanyName}
            onCustomerTypeChange={handleCustomerTypeChange}
            onCountryChange={handleCountryChange}
            onPhonePrefixChange={handlePhonePrefixChange}
            onPhoneNumberChange={handlePhoneNumberChange}
            onConsentChange={handleConsentChange}
            findCountryByCode={findCountryByCode}
          />
        )}

        {activeStep === 2 && (
          <Box sx={{ display: 'grid', gap: fieldGap }}>
            <TextInput
              label={t('auth:tenant_name')}
              value={form.tenant_name}
              onChange={handleChange('tenant_name')}
              required
              inputProps={{ 'data-testid': 'admin-trial-tenant-name' }}
            />
            <TextInput
              label={t('auth:tenant_slug')}
              value={form.tenant_slug}
              onChange={handleChange('tenant_slug')}
              helperText={t('onboarding:trial.tenant_slug_helper')}
            />
            <TextInput
              label={t('onboarding:trial.primary_domain_optional')}
              value={form.primary_domain}
              onChange={handleChange('primary_domain')}
              placeholder={preview?.suggested_domain}
            />
            <Select
              label={t('onboarding:trial.start_mode')}
              value={form.start_mode || 'trial'}
              onChange={handleChange('start_mode')}
            >
              <SelectOption value="trial">{t('onboarding:trial.start_mode_trial')}</SelectOption>
              <SelectOption value="direct">{t('onboarding:trial.start_mode_direct')}</SelectOption>
            </Select>
            {form.start_mode === 'direct' ? (
              <TextInput
                label={t('onboarding:trial.billing_amount')}
                type="number"
                inputProps={{ min: 0.01, step: 0.01 }}
                value={form.billing_amount}
                onChange={handleChange('billing_amount')}
                helperText={t('onboarding:trial.direct_access_note')}
              />
            ) : (
              <TextInput
                label={t('onboarding:trial.trial_days')}
                type="number"
                inputProps={{ min: 1, max: 90 }}
                value={form.trial_days}
                onChange={handleChange('trial_days')}
              />
            )}
            {previewLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                <CircularProgress size={18} sx={{ color: semantic.text.muted }} />
                <Typography sx={{ fontSize: 12, color: semantic.text.muted }}>
                  {t('common:loading')}
                </Typography>
              </Box>
            ) : preview ? (
              <Alert
                severity={preview.slug_available ? 'info' : 'warning'}
                sx={{
                  bgcolor: preview.slug_available
                    ? alpha(semantic.info, 0.1)
                    : alpha(semantic.warning, 0.1),
                  border: `1px solid ${preview.slug_available ? alpha(semantic.info, 0.45) : semantic.warning}`,
                  color: semantic.text.secondary,
                  '& .MuiAlert-icon': {
                    color: preview.slug_available ? semantic.info : semantic.warning,
                  },
                }}
              >
                {t('onboarding:trial.slug_preview', {
                  slug: preview.suggested_slug,
                  availability: preview.slug_available
                    ? t('onboarding:trial.slug_available')
                    : t('onboarding:trial.slug_taken'),
                })}
                <br />
                {t('onboarding:trial.domain_preview', {
                  domain: preview.suggested_domain,
                  availability: preview.domain_available
                    ? t('onboarding:trial.domain_available')
                    : t('onboarding:trial.domain_taken'),
                })}
                <br />
                {t('onboarding:trial.database_preview', {
                  name: preview.suggested_database_name,
                  mode: preview.provisioning_mode,
                })}
              </Alert>
            ) : null}
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ display: 'grid', gap: fieldGap }}>
            <Select
              fullWidth
              label={t('onboarding:trial.billing_period')}
              value={form.billing}
              onChange={handleChange('billing')}
              required
            >
              <SelectOption value="monthly">{t('onboarding:trial.billing_monthly')}</SelectOption>
              <SelectOption value="yearly">{t('onboarding:trial.billing_yearly')}</SelectOption>
            </Select>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.5 }}>
              {moduleCatalog.map((module) => (
                <FormControlLabel
                  key={module.public_code}
                  control={(
                    <Checkbox
                      checked={selectedModules.includes(module.public_code)}
                      onChange={(event) => {
                        setSelectedModules((current) => (
                          event.target.checked
                            ? [...current, module.public_code]
                            : current.filter((code) => code !== module.public_code)
                        ));
                      }}
                    />
                  )}
                  label={`${module.name} (${module.public_code})`}
                />
              ))}
            </Box>
            {pricePreview && (
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                {t('onboarding:trial.price_preview', {
                  amount: pricePreview.amount_due_for_selected_interval
                    ?? (form.billing === 'yearly' ? pricePreview.yearly_total : pricePreview.monthly_total),
                  currency: pricePreview.currency || 'EUR',
                })}
              </Typography>
            )}
          </Box>
        )}

        {activeStep === 3 && (
          <Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5,
                mb: 2.5,
              }}
            >
              <DetailField
                label={t('onboarding:trial.customer_type')}
                value={t(`onboarding:trial.customer_type_${form.customer_type}`)}
              />
              <DetailField
                label={t('onboarding:trial.review_customer')}
                value={form.company_name || (isConsumer ? form.contact_name : emptyValue)}
              />
              <DetailField
                label={t('onboarding:trial.review_admin')}
                value={`${form.contact_name || emptyValue} <${form.contact_email || emptyValue}>`}
              />
              <DetailField
                label={t('onboarding:trial.review_tenant')}
                value={`${form.tenant_name || emptyValue} / ${form.tenant_slug || preview?.suggested_slug || emptyValue}`}
              />
              <DetailField
                label={t('onboarding:trial.review_domain')}
                value={form.primary_domain || preview?.suggested_domain || emptyValue}
              />
              <DetailField
                label={t('onboarding:trial.review_modules')}
                value={selectedModules.join(', ') || emptyValue}
              />
              <DetailField
                label={t('onboarding:trial.billing_period')}
                value={t(`onboarding:trial.billing_${form.billing}`)}
              />
              <DetailField
                label={form.start_mode === 'direct' ? t('onboarding:trial.start_mode') : t('onboarding:trial.review_trial')}
                value={form.start_mode === 'direct'
                  ? `${t('onboarding:trial.start_mode_direct')} · ${form.billing_amount || emptyValue} EUR`
                  : t('onboarding:trial.review_trial_days', { days: form.trial_days })}
              />
            </Box>
            <SectionCard variant="Warning" sx={{ mb: 0 }}>
              <Typography sx={{ fontSize: 13, color: semantic.text.secondary, lineHeight: '20px' }}>
                {t('onboarding:trial.canonical_launch_note')}
              </Typography>
            </SectionCard>
          </Box>
        )}

        <WizardFooter
          activeStep={activeStep}
          submitting={submitting}
          canSubmit={selectedModules.length > 0 && Boolean(form.billing) && Boolean(form.tenant_name)}
          onBack={handleBack}
          onNext={handleNext}
          onSubmit={handleSubmit}
          backLabel={t('common:back')}
          nextLabel={t('onboarding:trial.next')}
          submitLabel={t('onboarding:trial.create_trial_tenant')}
        />
      </SectionCard>
    </Box>
  );
}
